import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// =====================================================
// Types
// =====================================================

// Global shopify object injected by App Bridge CDN script
declare global {
  interface Window {
    shopify?: {
      idToken: () => Promise<string>;
      config: {
        apiKey: string;
        shop: string;
      };
    };
  }
}

interface ShopifyContextValue {
  /** Whether the app is running inside Shopify Admin iframe */
  isEmbedded: boolean;
  /** Whether Shopify context is ready (App Bridge loaded + token exchange done) */
  isReady: boolean;
  /** Get a fresh session token for API calls (embedded mode only) */
  getSessionToken: () => Promise<string | null>;
  /** Error during initialization */
  error: string | null;
}

// =====================================================
// Public routes that should NEVER load App Bridge
// =====================================================

const PUBLIC_PATH_PREFIXES = ['/customize', '/login'];

function isPublicRoute(): boolean {
  return PUBLIC_PATH_PREFIXES.some(p => window.location.pathname.startsWith(p));
}

/**
 * Wait for App Bridge to initialize window.shopify (loaded synchronously via index.html).
 * Returns true if available, false if timeout.
 */
function waitForAppBridge(timeoutMs = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.shopify) {
      resolve(true);
      return;
    }
    const start = Date.now();
    const check = setInterval(() => {
      if (window.shopify) {
        clearInterval(check);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(check);
        resolve(false);
      }
    }, 50);
  });
}

// =====================================================
// Context
// =====================================================

const ShopifyContext = createContext<ShopifyContextValue>({
  isEmbedded: false,
  isReady: false,
  getSessionToken: async () => null,
  error: null,
});

export const useShopify = () => useContext(ShopifyContext);

// =====================================================
// Provider
// =====================================================

export const ShopifyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error] = useState<string | null>(null);
  const tokenExchangeDone = useRef(false);

  useEffect(() => {
    const inIframe = window.self !== window.top;

    // Skip App Bridge entirely for public routes (customizer, login, etc.)
    if (isPublicRoute()) {
      console.log('[Shopify] Public route — skipping App Bridge');
      setIsEmbedded(false);
      setIsReady(true);
      return;
    }

    // Inside an iframe — wait for App Bridge (loaded synchronously in index.html)
    if (inIframe) {
      console.log('[Shopify] Inside iframe, waiting for App Bridge...');

      waitForAppBridge(3000)
        .then((available) => {
          if (available) {
            console.log('[Shopify] App Bridge available');
            setIsEmbedded(true);
            return doTokenExchange();
          } else {
            console.warn('[Shopify] App Bridge not available after timeout');
            setIsEmbedded(false);
          }
        })
        .catch((err) => {
          console.warn('[Shopify] Init/token exchange issue:', err.message || err);
          if (window.shopify) {
            setIsEmbedded(true);
          }
        })
        .finally(() => {
          setIsReady(true);
        });
    } else {
      // Standalone mode (direct URL access, not in iframe)
      console.log('[Shopify] Standalone mode');
      setIsEmbedded(false);
      setIsReady(true);
    }
  }, []);

  const doTokenExchange = useCallback(async () => {
    if (tokenExchangeDone.current) return;
    if (!window.shopify?.idToken) return;

    const tokenPromise = window.shopify.idToken();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('idToken timeout (5s)')), 5000)
    );

    const token = await Promise.race([tokenPromise, timeoutPromise]);
    const response = await fetch('/auth/token-exchange', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    tokenExchangeDone.current = true;
  }, []);

  const getSessionToken = useCallback(async (): Promise<string | null> => {
    if (!window.shopify?.idToken) return null;
    try {
      const tokenPromise = window.shopify.idToken();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('idToken timeout')), 3000)
      );
      return await Promise.race([tokenPromise, timeoutPromise]);
    } catch {
      return null;
    }
  }, []);

  return (
    <ShopifyContext.Provider value={{ isEmbedded, isReady, getSessionToken, error }}>
      {children}
    </ShopifyContext.Provider>
  );
};
